// src/pages/PropertyForm.jsx
// ═══════════════════════════════════════════════════════════════════════════════
// V13 ENTERPRISE — Airbnb / Zillow / QuintoAndar Grade
// ═══════════════════════════════════════════════════════════════════════════════
// 🔥 V13: FIX CRÍTICO — uploadPendingImages() ahora recibe files DIRECTOS, no depende de estado React
// 🔥 V13: FIX CRÍTICO — previewImage se guarda como STRING explícito (no referencia)
// 🔥 V13: FIX CRÍTICO — Espera a DataContext.loadInitialData() antes de redirigir
// 🔥 V13: Nuevo helper _buildFinalImageList() construye el array desde previewItems (fuente única)
// 🔥 V13: uploadPendingImages() recibe File[] por parámetro, no lee pendingFiles del estado
// 🔥 V13: handleSubmit verifica que las imágenes se hayan persistido antes de navigate()
// 🔥 V13: Añadido setUploadStage("Verificando persistência…") para feedback visual completo
// 🔥 V12: FIX — previewImage se establece al guardar (previsualización instantánea)
// 🔥 V12: FIX — updateData incluye previewImage para evitar imágenes fantasma
// 🔥 V11: PREVIEW INSTANTÁNEO (0ms) — blob URLs se muestran antes de comprimir
// 🔥 V11: Compresión ASÍNCRONA en background (no bloquea UI)
// ═══════════════════════════════════════════════════════════════════════════════

import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../contexts/useAuth";
import { useIsMobile } from "../hooks/useMediaQuery";
import {
  ArrowLeft,
  Home,
  X,
  ImagePlus,
  Loader,
  CheckCircle,
  AlertCircle,
  Cloud,
  Globe,
  ImageOff,
  Clock,
  Zap,
  ShieldCheck,
} from "lucide-react";
import { uploadImages } from "../services/imageService";
import { publishProperty } from "../services/database";
import { getOptimizedCloudinaryUrl } from "../config/imageConfig";

// 🔥 V10: Importar DataContext como fuente única de verdad
import DataContext from "../lib/data/context/DataContext";

// ═══════════════════════════════════════════════════════════════════════════════
// 📐 CONSTANTES
// ═══════════════════════════════════════════════════════════════════════════════

const PROPERTY_TYPES  = ["Casa", "Apartamento", "Terreno", "Comercial", "Rural"];
const STATUS_OPTIONS  = ["Disponível", "Vendido", "Alugado", "Indisponível"];
const MAX_FILE_SIZE   = 10 * 1024 * 1024; // 10MB
const COMPRESS_WIDTH  = 1600;
const COMPRESS_QUALITY = 0.85;

const EMPTY_PROPERTY = {
  title: "", address: "", city: "", neighborhood: "", price: "",
  type: "Casa", bedrooms: "", bathrooms: "", area: "", status: "Disponível",
  featured: false, published: true, description: "", images: [],
  previewImage: null,
  iptu: "", condoFee: "",
};

// ═══════════════════════════════════════════════════════════════════════════════
// 🛠️ UTILITARIOS
// ═══════════════════════════════════════════════════════════════════════════════

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

/**
 * Comprime una imagen usando Canvas.toBlob() para menor uso de memoria
 */
function compressImage(file, maxWidth = COMPRESS_WIDTH, quality = COMPRESS_QUALITY) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onerror = () => resolve(file);
    reader.onload = (e) => {
      const img = new Image();
      img.onerror = () => resolve(file);
      img.onload = () => {
        try {
          let w = img.width, h = img.height;
          if (w > maxWidth) { h = Math.round((h * maxWidth) / w); w = maxWidth; }
          
          if (w === img.width && h === img.height && quality >= 0.9) {
            resolve(file);
            return;
          }
          
          const canvas = document.createElement("canvas");
          canvas.width = w; canvas.height = h;
          const ctx = canvas.getContext("2d");
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';
          ctx.drawImage(img, 0, 0, w, h);
          canvas.toBlob(
            (blob) => {
              if (!blob) { resolve(file); return; }
              resolve(new File([blob], file.name, { type: "image/jpeg", lastModified: Date.now() }));
            },
            "image/jpeg", quality
          );
        } catch (_) { resolve(file); }
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
}

/**
 * 🔥 V13: Resuelve el ID real de una propiedad recién creada
 * con verificación de persistencia en DataContext
 */
async function resolveCreatedPropertyId(rawId, title, ctx, userId, maxWaitMs = 8000) {
  const start = Date.now();
  while (Date.now() - start < maxWaitMs) {
    const syncManager = ctx?.syncManager;
    const resolvedId = syncManager?.resolveRealId?.(rawId) ?? rawId;
    
    // Verificar en DataContext (fuente principal)
    const found = ctx?.collections?.properties?.findById?.(resolvedId);
    if (found) return { id: resolvedId, property: found };
    
    if (resolvedId !== rawId) {
      const foundOrig = ctx?.collections?.properties?.findById?.(rawId);
      if (foundOrig) return { id: rawId, property: foundOrig };
    }
    
    // Verificar en localStorage (respaldo)
    try {
      const dbKey = `imoveispro_db_user_${userId}`;
      const stored = JSON.parse(localStorage.getItem(dbKey) ?? "{}");
      const props = stored.properties ?? [];
      const match = props
        .filter((p) => p?.title === title)
        .sort((a, b) => new Date(b.createdAt ?? 0) - new Date(a.createdAt ?? 0))[0];
      if (match) return { id: match.id ?? match._id, property: match };
    } catch (_) {}
    
    await sleep(250);
  }
  return { id: rawId, property: null };
}

// ═══════════════════════════════════════════════════════════════════════════════
// 🧩 SUB-COMPONENTES
// ═══════════════════════════════════════════════════════════════════════════════

function Field({ label, required, hint, children, isMobile }) {
  return (
    <div className="space-y-1.5">
      <label className={`font-medium text-secondary flex items-center gap-1 ${isMobile ? "text-xs" : "text-sm"}`}>
        {label}{required && <span className="text-rose-500">*</span>}
      </label>
      {hint && <p className={`text-muted ${isMobile ? "text-[10px]" : "text-xs"}`}>{hint}</p>}
      {children}
    </div>
  );
}

const INPUT_CLS = (isMobile) =>
  `surface-input w-full ${isMobile ? "px-3 py-2.5 text-[16px]" : "px-4 py-3 text-sm"} outline-none`;

function CheckboxField({ name, checked, onChange, label, icon, isMobile }) {
  return (
    <label className="flex items-center gap-2 cursor-pointer select-none">
      <input type="checkbox" name={name} checked={checked} onChange={onChange}
        className="w-5 h-5 text-blue-600 rounded border-slate-300 focus:ring-blue-200" />
      <span className={`text-secondary flex items-center gap-1 ${isMobile ? "text-sm" : "text-sm"}`}>
        {icon}{label}
      </span>
    </label>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// 🏠 COMPONENTE PRINCIPAL — V13
// ═══════════════════════════════════════════════════════════════════════════════

export default function PropertyForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { database, showToast, user } = useAuth();
  const isMobile = useIsMobile();
  const fileInputRef = useRef(null);
  
  const compressAbortRef = useRef(false);

  const isEditMode = Boolean(id);
  const currentUserId = user?.uid ?? database?.userId ?? "user";
  const currentCompanyId = user?.companyId ?? database?.companyId ?? `company_${currentUserId}`;

  const authUserId = useMemo(() => {
    try {
      const stored = localStorage.getItem('auth_user');
      return stored ? JSON.parse(stored).id : user?.uid || null;
    } catch { return user?.uid || null; }
  }, [user?.uid]);

  const ctxRef = useRef(null);
  const getContext = useCallback(() => {
    if (ctxRef.current) return ctxRef.current;
    if (authUserId) {
      ctxRef.current = DataContext.getInstance(authUserId);
      return ctxRef.current;
    }
    return null;
  }, [authUserId]);

  // ═══════════════════════════════════════════════════════════════════════════
  // 📦 ESTADO
  // ═══════════════════════════════════════════════════════════════════════════
  const [property, setProperty] = useState(EMPTY_PROPERTY);
  const [previewItems, setPreviewItems] = useState([]);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [originalCloudUrls, setOriginalCloudUrls] = useState([]);
  const [imageErrors, setImageErrors] = useState({});
  const [compressing, setCompressing] = useState(false);
  const [compressProgress, setCompressProgress] = useState({ current: 0, total: 0 });
  const [saving, setSaving] = useState(false);
  const [uploadStage, setUploadStage] = useState("");
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isPublishing, setIsPublishing] = useState(false);
  const [errors, setErrors] = useState({});
  const [globalError, setGlobalError] = useState(null);

  // 🔥 V13: Ref para los archivos pendientes (no depende de estado React para submit)
  const pendingFilesRef = useRef([]);
  
  const toastRef = useRef(showToast);
  useEffect(() => { toastRef.current = showToast; }, [showToast]);
  const toast = useCallback((msg, type) => toastRef.current?.(msg, type), []);

  // ═══════════════════════════════════════════════════════════════════════════
  // 📥 CARGA INICIAL (MODO EDICIÓN)
  // ═══════════════════════════════════════════════════════════════════════════
  useEffect(() => {
    if (!id) return;
    
    let cancelled = false;
    
    const loadProperty = async () => {
      try {
        const ctx = getContext();
        
        let data = null;
        if (ctx?.collections?.properties?.findById) {
          data = await ctx.collections.properties.findById(id);
        }
        if (!data && database?.getPropertyById) {
          data = database.getPropertyById(id);
        }

        if (cancelled) return;

        if (!data) {
          toast("Imóvel não encontrado", "error");
          navigate("/admin/imoveis");
          return;
        }

        const cloudUrls = (data.images ?? []).filter(
          (u) => typeof u === "string" && (u.startsWith("http://") || u.startsWith("https://"))
        );

        const cloudItems = cloudUrls.map((url, i) => ({
          id: `cloud_${i}_${Date.now()}`,
          url: (() => {
            try { return getOptimizedCloudinaryUrl(url, "thumbnail") || url; }
            catch { return url; }
          })(),
          originalUrl: url,
          name: `Imagem ${i + 1}`,
          size: 0,
          status: 'cloud',
        }));

        console.log(`🖼️ [PropertyForm V13] ${cloudItems.length} imagens Cloudinary carregadas`);

        setOriginalCloudUrls(cloudUrls);
        setPreviewItems(cloudItems);
        setProperty({ ...EMPTY_PROPERTY, ...data, images: cloudUrls, previewImage: data.previewImage || cloudUrls[0] || null });
        setImageErrors({});
      } catch (err) {
        if (!cancelled) {
          console.error("❌ PropertyForm: erro ao carregar:", err);
          setGlobalError("Erro ao carregar imóvel.");
        }
      }
    };

    loadProperty();
    
    return () => { cancelled = true; };
  }, [id, getContext, navigate, toast, database]);

  // ═══════════════════════════════════════════════════════════════════════════
  // 🧹 LIMPIEZA DE BLOB URLs AL DESMONTAR
  // ═══════════════════════════════════════════════════════════════════════════
  const previewItemsRef = useRef(previewItems);
  useEffect(() => { previewItemsRef.current = previewItems; }, [previewItems]);
  
  useEffect(() => {
    return () => {
      previewItemsRef.current.forEach((item) => {
        if (item.url?.startsWith("blob:")) URL.revokeObjectURL(item.url);
        if (item.blobUrl?.startsWith("blob:")) URL.revokeObjectURL(item.blobUrl);
      });
      compressAbortRef.current = true;
    };
  }, []);

  // ═══════════════════════════════════════════════════════════════════════════
  // ✋ HANDLERS DE FORMULARIO
  // ═══════════════════════════════════════════════════════════════════════════

  const handleChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    setProperty((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : (value ?? "") }));
    setErrors((prev) => {
      if (!prev[name]) return prev;
      const n = { ...prev };
      delete n[name];
      return n;
    });
  }, []);

  // ═══════════════════════════════════════════════════════════════════════════
  // 🖼️ SELECCIÓN DE IMÁGENES — PREVIEW INSTANTÁNEO + COMPRESIÓN BACKGROUND
  // ═══════════════════════════════════════════════════════════════════════════

  const handleImages = useCallback(async (e) => {
    const files = Array.from(e.target.files ?? []);
    if (!files.length) return;

    const oversized = files.filter((f) => f.size > MAX_FILE_SIZE);
    if (oversized.length) {
      toast(`${oversized.length} imagem(ns) excedem 10MB e foram ignoradas.`, "warning");
    }
    const validFiles = files.filter((f) => f.size <= MAX_FILE_SIZE);
    if (!validFiles.length) return;

    const instantPreviews = validFiles.map((file, i) => ({
      id: `blob_${Date.now()}_${i}_${Math.random().toString(36).substr(2, 6)}`,
      url: URL.createObjectURL(file),
      name: file.name,
      size: file.size,
      file,
      status: 'blob',
    }));

    setPreviewItems((prev) => [...prev, ...instantPreviews]);
    
    // 🔥 V13: Establecer previewImage inmediatamente con el primer blob
    if (instantPreviews.length > 0) {
      setProperty((prev) => ({ ...prev, previewImage: instantPreviews[0].url }));
    }
    
    if (fileInputRef.current) fileInputRef.current.value = "";

    compressAbortRef.current = false;
    setCompressing(true);
    setCompressProgress({ current: 0, total: validFiles.length });
    setGlobalError(null);

    const compressedFiles = [];
    
    for (let i = 0; i < validFiles.length; i++) {
      if (compressAbortRef.current) break;
      
      const file = validFiles[i];
      const previewId = instantPreviews[i].id;
      
      setPreviewItems((prev) => 
        prev.map((item) => 
          item.id === previewId ? { ...item, status: 'compressing' } : item
        )
      );
      
      try {
        const compressed = await compressImage(file);
        compressedFiles.push(compressed);
        setCompressProgress({ current: i + 1, total: validFiles.length });
      } catch (err) {
        console.warn(`⚠️ Erro ao comprimir ${file.name}:`, err);
        compressedFiles.push(file);
      }
    }

    if (!compressAbortRef.current) {
      setPreviewItems((prev) => 
        prev.map((item) => 
          item.status === 'compressing' ? { ...item, status: 'compressed' } : item
        )
      );
      
      // 🔥 V13: Guardar en ref Y en estado para consistencia
      pendingFilesRef.current = [...pendingFilesRef.current, ...compressedFiles];
      setPendingFiles(pendingFilesRef.current);
      
      toast(`${validFiles.length} imagem(ns) pronta(s)`, "success");
    }

    setCompressing(false);
    setCompressProgress({ current: 0, total: 0 });
  }, [toast]);

  // ═══════════════════════════════════════════════════════════════════════════
  // 🗑️ ELIMINAR IMAGEN
  // ═══════════════════════════════════════════════════════════════════════════

  const removeImage = useCallback((previewId) => {
    setPreviewItems((prev) => {
      const item = prev.find((p) => p.id === previewId);
      if (item) {
        if (item.url?.startsWith("blob:")) URL.revokeObjectURL(item.url);
        if (item.blobUrl?.startsWith("blob:")) URL.revokeObjectURL(item.blobUrl);
        
        // 🔥 V13: También eliminar de pendingFilesRef
        if (item.file) {
          pendingFilesRef.current = pendingFilesRef.current.filter(
            (f) => !(f.name === item.file.name && f.size === item.file.size)
          );
          setPendingFiles(pendingFilesRef.current);
        }
      }
      const remaining = prev.filter((p) => p.id !== previewId);
      
      // Actualizar previewImage si se eliminó la primera imagen
      if (remaining.length > 0 && remaining[0].url) {
        setProperty((propPrev) => ({ ...propPrev, previewImage: remaining[0].url }));
      } else {
        setProperty((propPrev) => ({ ...propPrev, previewImage: null }));
      }
      
      return remaining;
    });

    setImageErrors((prev) => {
      const n = { ...prev };
      delete n[previewId];
      return n;
    });
  }, []);

  // Sincronizar pendingFiles del estado con previewItems
  useEffect(() => {
    const compressedItems = previewItems.filter(
      (item) => item.status === 'compressed' && item.file
    );
    const newFiles = compressedItems
      .filter((item) => !pendingFilesRef.current.some((pf) => pf.name === item.name && pf.size === item.size))
      .map((item) => item.file);
    
    if (newFiles.length > 0) {
      pendingFilesRef.current = [...pendingFilesRef.current, ...newFiles];
      setPendingFiles(pendingFilesRef.current);
    }
  }, [previewItems]);

  // ═══════════════════════════════════════════════════════════════════════════
  // 🔥 V13: CONSTRUIR LISTA FINAL DE IMÁGENES DESDE previewItems
  // ═══════════════════════════════════════════════════════════════════════════
  
  /**
   * Construye el array final de URLs de imágenes combinando:
   * - URLs de Cloudinary ya existentes (cloudItems)
   * - URLs recién subidas (newUrls)
   * Retorna un array de strings limpio
   */
  const _buildFinalImageList = useCallback((newUrls) => {
    const cloudUrls = previewItems
      .filter((item) => item.status === 'cloud')
      .map((item) => item.originalUrl || item.url)
      .filter((url) => typeof url === 'string' && url.startsWith('http'));
    
    return [...cloudUrls, ...newUrls];
  }, [previewItems]);

  // ═══════════════════════════════════════════════════════════════════════════
  // ☁️ UPLOAD A CLOUDINARY — V13: Recibe files por parámetro
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * 🔥 V13: Sube archivos a Cloudinary.
   * AHORA recibe los File[] directamente, NO depende del estado pendingFiles.
   * Esto elimina la condición de carrera con el estado de React.
   */
  const uploadPendingImages = useCallback(async (filesToUpload) => {
    if (!filesToUpload || filesToUpload.length === 0) return [];
    
    const tempId = `temp_${Date.now()}`;
    setUploadStage(`Enviando ${filesToUpload.length} imagem(ns)…`);
    
    const uploaded = await uploadImages(filesToUpload, tempId, currentUserId, (progress) => {
      if (progress.percent != null) setUploadProgress(progress.percent);
      if (progress.stage === "uploading" && progress.uploading) {
        setUploadStage(`Enviando: ${progress.uploading}`);
      }
    });
    
    console.log(`☁️ [V13] ${uploaded.length} imagens enviadas para Cloudinary:`, uploaded);
    return uploaded;
  }, [currentUserId]);

  // ═══════════════════════════════════════════════════════════════════════════
  // 📤 PUBLICAR EN VITRINA
  // ═══════════════════════════════════════════════════════════════════════════

  const publishToShowcase = useCallback(async (propertyData) => {
    if (!propertyData?.published) return null;
    setIsPublishing(true);
    setUploadStage("Publicando na vitrine…");
    try {
      const ctx = getContext();
      const company = ctx?.getUser?.() || database?.getCompany?.();
      const published = publishProperty(propertyData, company);
      if (published) toast("✅ Publicado na vitrine! Link disponível.", "success");
      return published ?? null;
    } catch (err) {
      console.error("❌ publishToShowcase:", err);
      toast("⚠️ Imóvel salvo mas não publicado.", "warning");
      return null;
    } finally {
      setIsPublishing(false);
    }
  }, [database, toast, getContext]);

  // ═══════════════════════════════════════════════════════════════════════════
  // ✅ VALIDACIÓN
  // ═══════════════════════════════════════════════════════════════════════════

  const validate = useCallback(() => {
    const errs = {};
    if (!property.title.trim()) errs.title = "Título obrigatório";
    if (!property.type) errs.type = "Tipo obrigatório";
    if (!property.status) errs.status = "Status obrigatório";
    if (property.price && isNaN(Number(String(property.price).replace(/\D/g, "")))) {
      errs.price = "Preço inválido";
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }, [property]);

  // ═══════════════════════════════════════════════════════════════════════════
  // 💾 SUBMIT — V13 ULTRA-ROBUSTO
  // ═══════════════════════════════════════════════════════════════════════════

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (!validate()) { toast("Corrija os campos obrigatórios", "warning"); return; }

    setSaving(true);
    setGlobalError(null);
    setUploadProgress(0);
    setUploadStage("");

    try {
      const ctx = getContext();
      
      // ═══════════════════════════════════════════════════════════════════
      // 🔥 V13: Obtener archivos DIRECTAMENTE de previewItems (no del estado)
      // ═══════════════════════════════════════════════════════════════════
      const pendingItems = previewItems.filter(
        (item) => item.status === 'blob' || item.status === 'compressed' || item.status === 'compressing'
      );
      
      // Construir lista de archivos directamente desde previewItems
      const filesToUpload = pendingItems
        .filter((item) => item.file instanceof File)
        .map((item) => item.file);
      
      console.log(`📤 [V13] Arquivos para upload: ${filesToUpload.length}`, 
        filesToUpload.map(f => f.name));

      // ═══════════════════════════════════════════════════════════════════
      // MODO EDICIÓN
      // ═══════════════════════════════════════════════════════════════════
      if (isEditMode) {
        const existingCloudUrls = previewItems
          .filter((item) => item.status === 'cloud')
          .map((item) => item.originalUrl || item.url);

        let newUrls = [];
        if (filesToUpload.length > 0) {
          newUrls = await uploadPendingImages(filesToUpload);
        }
        
        const finalImages = [...existingCloudUrls, ...newUrls];
        
        // 🔥 V13: previewImage como STRING explícito
        const previewImageString = finalImages.length > 0 ? String(finalImages[0]) : null;
        
        const updateData = {
          ...property,
          images: finalImages,
          previewImage: previewImageString,
          updatedAt: new Date().toISOString(),
          updatedBy: currentUserId,
        };

        console.log(`📝 [V13] Atualizando propriedade ${id}:`, {
          imagesCount: finalImages.length,
          previewImage: previewImageString?.substring(0, 60) + '...',
        });

        if (ctx?.collections?.properties?.update) {
          await ctx.collections.properties.update(id, updateData);
          // 🔥 V13: Forzar refresh del DataContext
          await ctx.refreshData();
        } else {
          database?.updateProperty?.(id, updateData);
        }

        // Limpiar blobs
        previewItems.forEach((item) => {
          if (item.url?.startsWith("blob:")) URL.revokeObjectURL(item.url);
        });

        toast("✅ Imóvel atualizado com sucesso!", "success");
        navigate(`/admin/imoveis/${id}`);
        return;
      }

      // ═══════════════════════════════════════════════════════════════════
      // MODO CREACIÓN
      // ═══════════════════════════════════════════════════════════════════
      let finalImages = [];
      
      if (filesToUpload.length > 0) {
        setUploadStage("Enviando imagens para Cloudinary…");
        finalImages = await uploadPendingImages(filesToUpload);
      }
      
      // 🔥 V13: previewImage como STRING explícito (garantiza persistencia)
      const previewImageString = finalImages.length > 0 ? String(finalImages[0]) : null;
      
      const newPropertyData = {
        ...property,
        images: finalImages,
        previewImage: previewImageString,
        userId: currentUserId,
        createdBy: currentUserId,
        ownerId: currentUserId,
        companyId: currentCompanyId,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      console.log(`🏗️ [V13] Criando nova propriedade:`, {
        title: property.title,
        imagesCount: finalImages.length,
        previewImage: previewImageString?.substring(0, 60) + '...',
        images: finalImages.map(u => typeof u === 'string' ? u.substring(0, 40) + '...' : typeof u),
      });

      setUploadStage("Salvando imóvel…");
      
      let createdRaw;
      if (ctx?.collections?.properties?.create) {
        createdRaw = await ctx.collections.properties.create(newPropertyData);
        // 🔥 V13: CRÍTICO — Forzar refresh inmediato para asegurar persistencia
        await ctx.refreshData();
      } else {
        createdRaw = await database?.createProperty?.(newPropertyData);
      }
      
      const rawId = createdRaw?.id ?? createdRaw?._id ?? createdRaw;

      setUploadStage("Verificando persistência…");
      
      // 🔥 V13: Esperar a que el ID se resuelva y los datos estén disponibles
      const { id: resolvedId, property: savedProperty } = await resolveCreatedPropertyId(
        String(rawId ?? ""), property.title, ctx, currentUserId, 8000
      );

      console.log(`✅ [V13] Propriedade persistida: ${resolvedId}`, {
        savedImages: savedProperty?.images?.length || 0,
        savedPreview: savedProperty?.previewImage?.substring(0, 40) + '...',
      });

      if (resolvedId) {
        sessionStorage.setItem("last_created_property_id", resolvedId);
        sessionStorage.setItem("last_created_title", property.title);
      }
      
      if (property.published) {
        const toPublish = savedProperty ?? { ...newPropertyData, id: resolvedId };
        await publishToShowcase(toPublish);
      }
      
      // Limpiar blobs
      previewItems.forEach((item) => {
        if (item.url?.startsWith("blob:")) URL.revokeObjectURL(item.url);
        if (item.blobUrl?.startsWith("blob:")) URL.revokeObjectURL(item.blobUrl);
      });
      
      toast("✅ Imóvel criado com sucesso!", "success");
      
      // 🔥 V13: Redirigir con replace para evitar problemas de historial
      navigate("/admin/imoveis?created=success", { replace: true });

    } catch (err) {
      console.error("❌ [V13] handleSubmit error:", err);
      setGlobalError("Erro ao salvar imóvel. Verifique sua conexão.");
      toast("Erro ao salvar imóvel", "error");
    } finally {
      setSaving(false);
      setUploadProgress(0);
      setUploadStage("");
      setIsPublishing(false);
    }
  }, [
    validate, isEditMode, previewItems, uploadPendingImages, 
    property, database, id, currentUserId, currentCompanyId,
    publishToShowcase, navigate, toast, getContext,
  ]);

  // ═══════════════════════════════════════════════════════════════════════════
  // 📊 DERIVADOS
  // ═══════════════════════════════════════════════════════════════════════════

  const isBusy = saving || compressing;

  const uploadSummary = useMemo(() => {
    const cloudCount = previewItems.filter((item) => item.status === 'cloud').length;
    const blobCount = previewItems.filter((item) => item.status === 'blob').length;
    const compressingCount = previewItems.filter((item) => item.status === 'compressing').length;
    const compressedCount = previewItems.filter((item) => item.status === 'compressed').length;
    const pendingTotal = blobCount + compressingCount + compressedCount;
    
    if (!cloudCount && !pendingTotal) return null;
    const parts = [];
    if (cloudCount) parts.push(`☁️ ${cloudCount} CDN`);
    if (pendingTotal) parts.push(`⏳ ${pendingTotal} pendente(s)`);
    return parts.join("  •  ");
  }, [previewItems]);

  const totalPreviews = previewItems.length;

  // ═══════════════════════════════════════════════════════════════════════════
  // 🎨 RENDER
  // ═══════════════════════════════════════════════════════════════════════════

  return (
    <div className={isMobile ? "p-3 space-y-4 pb-8" : "p-6 space-y-6 max-w-4xl mx-auto"}>

      {/* Header */}
      <div className="flex items-center justify-between">
        <button type="button" onClick={() => navigate("/admin/imoveis")}
          className="flex items-center gap-2 text-secondary hover:text-primary group transition-colors">
          <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
          <span className={`font-medium ${isMobile ? "text-sm" : ""}`}>
            {isMobile ? "Voltar" : "Voltar para Imóveis"}
          </span>
        </button>
        <span className={`text-muted ${isMobile ? "text-xs" : "text-sm"}`}>
          {isEditMode ? "Editando imóvel" : "Novo imóvel"}
        </span>
      </div>

      {/* Título */}
      <div className="flex items-center gap-3">
        <div className={`${isMobile ? "p-2" : "p-3"} bg-blue-50 dark:bg-blue-500/15 rounded-xl`}>
          <Home className={`${isMobile ? "w-5 h-5" : "w-6 h-6"} text-blue-600`} />
        </div>
        <h1 className={`font-bold text-primary ${isMobile ? "text-2xl" : "text-3xl"}`}>
          {isEditMode ? (isMobile ? "Editar" : "Editar Imóvel") : (isMobile ? "Novo" : "Novo Imóvel")}
        </h1>
      </div>

      {/* Error global */}
      {globalError && (
        <div className="bg-rose-50 border border-rose-200 rounded-xl p-4 flex items-start gap-3 text-rose-700">
          <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <p className="text-sm flex-1">{globalError}</p>
          <button type="button" onClick={() => setGlobalError(null)}><X className="w-4 h-4" /></button>
        </div>
      )}

      {/* Progreso de upload */}
      {saving && uploadStage && (
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4 space-y-2">
          <div className="flex items-center gap-2 text-sm text-blue-700">
            {isPublishing ? <Globe className="w-4 h-4 animate-pulse" /> : 
             uploadStage.includes("Verificando") ? <ShieldCheck className="w-4 h-4 animate-pulse" /> :
             <Cloud className="w-4 h-4 animate-pulse" />}
            <span>{uploadStage}</span>
          </div>
          {uploadProgress > 0 && (
            <div className="w-full h-2 bg-blue-100 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-blue-600 to-indigo-600 transition-all duration-300"
                style={{ width: `${uploadProgress}%` }} />
            </div>
          )}
        </div>
      )}

      {/* Formulario */}
      <form onSubmit={handleSubmit} noValidate className={isMobile ? "space-y-4" : "space-y-6"}>
        <div className={`surface-card ${isMobile ? "p-4 space-y-4" : "p-6 space-y-6"}`}>

          <div className={`grid ${isMobile ? "grid-cols-1 gap-4" : "grid-cols-1 md:grid-cols-2 gap-6"}`}>
            <Field label="Título" required isMobile={isMobile}>
              <input name="title" value={property.title} onChange={handleChange}
                placeholder="Ex: Apartamento na Vila Mariana"
                className={`${INPUT_CLS(isMobile)} ${errors.title ? "border-rose-400" : ""}`} />
              {errors.title && <p className="text-rose-500 text-xs mt-1">{errors.title}</p>}
            </Field>
            <Field label="Tipo" required isMobile={isMobile}>
              <select name="type" value={property.type} onChange={handleChange} className={INPUT_CLS(isMobile)}>
                {PROPERTY_TYPES.map((t) => <option key={t}>{t}</option>)}
              </select>
            </Field>
            <Field label="Endereço" isMobile={isMobile}>
              <input name="address" value={property.address} onChange={handleChange}
                placeholder="Rua, número" className={INPUT_CLS(isMobile)} />
            </Field>
            <Field label="Cidade" isMobile={isMobile}>
              <input name="city" value={property.city} onChange={handleChange}
                placeholder="São Paulo" className={INPUT_CLS(isMobile)} />
            </Field>
            <Field label="Bairro" isMobile={isMobile}>
              <input name="neighborhood" value={property.neighborhood} onChange={handleChange}
                placeholder="Vila Mariana" className={INPUT_CLS(isMobile)} />
            </Field>
            <Field label="Preço (R$)" isMobile={isMobile}>
              <input name="price" value={property.price} onChange={handleChange}
                placeholder="500000" inputMode="numeric"
                className={`${INPUT_CLS(isMobile)} ${errors.price ? "border-rose-400" : ""}`} />
              {errors.price && <p className="text-rose-500 text-xs mt-1">{errors.price}</p>}
            </Field>
            <Field label="Dormitórios" isMobile={isMobile}>
              <input name="bedrooms" value={property.bedrooms} onChange={handleChange}
                placeholder="3" type="number" min="0" className={INPUT_CLS(isMobile)} />
            </Field>
            <Field label="Banheiros" isMobile={isMobile}>
              <input name="bathrooms" value={property.bathrooms} onChange={handleChange}
                placeholder="2" type="number" min="0" className={INPUT_CLS(isMobile)} />
            </Field>
            <Field label="Área (m²)" isMobile={isMobile}>
              <input name="area" value={property.area} onChange={handleChange}
                placeholder="120" type="number" min="0" className={INPUT_CLS(isMobile)} />
            </Field>
            <Field label="Status" required isMobile={isMobile}>
              <select name="status" value={property.status} onChange={handleChange} className={INPUT_CLS(isMobile)}>
                {STATUS_OPTIONS.map((s) => <option key={s}>{s}</option>)}
              </select>
            </Field>
            <Field label="IPTU (R$/ano)" isMobile={isMobile}>
              <input name="iptu" value={property.iptu} onChange={handleChange}
                placeholder="2500" inputMode="numeric" className={INPUT_CLS(isMobile)} />
            </Field>
            <Field label="Condomínio (R$/mês)" isMobile={isMobile}>
              <input name="condoFee" value={property.condoFee} onChange={handleChange}
                placeholder="800" inputMode="numeric" className={INPUT_CLS(isMobile)} />
            </Field>
            <div className={`${isMobile ? "col-span-1" : "col-span-2"} flex flex-wrap gap-6`}>
              <CheckboxField name="featured" checked={property.featured} onChange={handleChange}
                label="⭐ Destacar imóvel" isMobile={isMobile} />
              <CheckboxField name="published" checked={property.published !== false} onChange={handleChange}
                label="Publicar na vitrine (link público)" icon={<Globe className="w-4 h-4" />} isMobile={isMobile} />
            </div>
          </div>

          <Field label="Descrição" isMobile={isMobile}>
            <textarea name="description" value={property.description} onChange={handleChange}
              rows={isMobile ? 4 : 5} placeholder="Descreva o imóvel..."
              className={`${INPUT_CLS(isMobile)} resize-y`} />
          </Field>

          {/* UPLOAD DE IMÁGENES — V13 PREVIEW INSTANTÁNEO */}
          <div className="space-y-4">
            <Field label="Imagens do Imóvel" hint="☁️ Preview instantâneo • Upload ao salvar" isMobile={isMobile}>
              <button type="button" onClick={() => fileInputRef.current?.click()} disabled={isBusy}
                className={`flex items-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-xl transition-all disabled:opacity-50 shadow-md hover:shadow-lg ${
                  isMobile ? "px-4 py-2 text-sm" : "px-6 py-3"}`}>
                <ImagePlus className="w-4 h-4" />
                {compressing ? "Comprimindo…" : isMobile ? "Adicionar fotos" : "Selecionar Imagens"}
              </button>
            </Field>
            <input ref={fileInputRef} type="file" multiple accept="image/*"
              onChange={handleImages} className="hidden" disabled={isBusy} />

            {compressing && compressProgress.total > 0 && (
              <div className="space-y-1.5">
                <div className="flex items-center gap-2 text-sm text-blue-600">
                  <Zap className="w-4 h-4" />
                  <span>Otimizando… {compressProgress.current}/{compressProgress.total}</span>
                </div>
                <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full bg-blue-600 transition-all duration-300"
                    style={{ width: `${Math.round((compressProgress.current / compressProgress.total) * 100)}%` }} />
                </div>
              </div>
            )}

            {previewItems.length > 0 && (
              <>
                <div className={`grid ${isMobile ? "grid-cols-2 gap-2" : "grid-cols-2 md:grid-cols-4 gap-4"}`}>
                  {previewItems.map((item, index) => {
                    const isCloud = item.status === 'cloud';
                    const isBlob = item.status === 'blob';
                    const isCompressing = item.status === 'compressing';
                    const isCompressed = item.status === 'compressed';
                    const hasError = imageErrors[item.id];
                    
                    return (
                      <div key={item.id} className="relative group aspect-square">
                        {hasError ? (
                          <div className="w-full h-full bg-muted rounded-xl border border-color flex flex-col items-center justify-center gap-1">
                            <ImageOff className="w-8 h-8 text-muted" />
                            <span className="text-[10px] text-muted">Erro</span>
                          </div>
                        ) : (
                          <div className="relative w-full h-full">
                            <img
                              src={item.url}
                              alt={item.name || `Preview ${index + 1}`}
                              className="w-full h-full object-cover rounded-xl border border-color bg-muted"
                              onError={() => {
                                setImageErrors((prev) => ({ ...prev, [item.id]: true }));
                              }}
                              loading="lazy"
                              decoding="async"
                            />
                            {isCompressing && (
                              <div className="absolute inset-0 bg-black/30 rounded-xl flex items-center justify-center">
                                <div className="flex flex-col items-center gap-1">
                                  <Loader className="w-6 h-6 text-white animate-spin" />
                                  <span className="text-white text-[9px] font-medium">Otimizando</span>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                        
                        <button type="button" onClick={() => removeImage(item.id)} disabled={isBusy}
                          className="absolute top-2 right-2 w-8 h-8 flex items-center justify-center bg-rose-600 hover:bg-rose-700 text-white rounded-full shadow-md opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-all z-10 active:scale-95"
                          title="Remover imagem">
                          <X className="w-3.5 h-3.5" />
                        </button>
                        
                        {isCloud && !hasError && (
                          <div className="absolute bottom-2 left-2 bg-green-500/90 text-white text-[9px] px-1.5 py-0.5 rounded-full flex items-center gap-0.5">
                            <Cloud className="w-2.5 h-2.5" /> CDN
                          </div>
                        )}
                        {isBlob && !hasError && (
                          <div className="absolute bottom-2 left-2 bg-amber-500/90 text-white text-[9px] px-1.5 py-0.5 rounded-full flex items-center gap-0.5">
                            <Clock className="w-2.5 h-2.5" /> Pendente
                          </div>
                        )}
                        {isCompressing && (
                          <div className="absolute bottom-2 left-2 bg-blue-500/90 text-white text-[9px] px-1.5 py-0.5 rounded-full flex items-center gap-0.5">
                            <Loader className="w-2.5 h-2.5 animate-spin" /> Otimizando
                          </div>
                        )}
                        {isCompressed && !hasError && (
                          <div className="absolute bottom-2 left-2 bg-emerald-500/90 text-white text-[9px] px-1.5 py-0.5 rounded-full flex items-center gap-0.5">
                            <CheckCircle className="w-2.5 h-2.5" /> Pronto
                          </div>
                        )}
                        
                        {index === 0 && (
                          <div className="absolute top-2 left-2 z-10">
                            <span className={`px-1.5 py-0.5 bg-gradient-to-r from-orange-500 to-pink-500 text-white font-semibold rounded-full ${
                              isMobile ? 'text-[8px]' : 'text-[10px]'
                            }`}>
                              {isMobile ? 'Pri' : 'Principal'}
                            </span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                
                {uploadSummary && (
                  <p className={`text-muted ${isMobile ? "text-[10px]" : "text-xs"}`}>
                    {totalPreviews} {totalPreviews === 1 ? "imagem" : "imagens"}  •  {uploadSummary}
                  </p>
                )}
              </>
            )}
            
            {previewItems.length === 0 && !compressing && (
              <div className="border-2 border-dashed border-slate-200 rounded-xl p-8 text-center text-muted">
                <ImagePlus className="w-10 h-10 mx-auto mb-2 opacity-50" />
                <p className="text-sm">Nenhuma imagem adicionada</p>
                <p className="text-xs mt-1">Clique em "Selecionar Imagens" para começar</p>
              </div>
            )}
          </div>
        </div>

        {/* Botones */}
        <div className={`flex ${isMobile ? "gap-2" : "gap-4"} justify-end`}>
          <button type="button" onClick={() => navigate("/admin/imoveis")} disabled={isBusy}
            className={`border border-slate-300 rounded-xl text-secondary hover:bg-slate-50 transition-all disabled:opacity-50 ${
              isMobile ? "px-4 py-2 text-sm flex-1" : "px-6 py-3"}`}>Cancelar</button>
          <button type="submit" disabled={isBusy}
            className={`bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-xl transition-all disabled:opacity-60 flex items-center gap-2 shadow-md ${
              isMobile ? "px-4 py-2 text-sm flex-1 justify-center" : "px-6 py-3"}`}>
            {saving ? (
              <><Loader className="w-4 h-4 animate-spin" />{isPublishing ? "Publicando…" : uploadStage ? "Enviando…" : "Salvando…"}</>
            ) : (
              <><CheckCircle className="w-4 h-4" />{isEditMode ? (isMobile ? "Atualizar" : "Atualizar Imóvel") : (isMobile ? "Criar" : "Criar Imóvel")}</>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}